import { useEffect } from "react";
import { recordWantAdReadingTime } from "@/services/v2/wantAdService";
import { getReachViewerKey } from "@/helpers/reachViewer";

export const READING_CAP_MS = 10 * 60 * 1000;
export const READING_MIN_SECONDS = 2;

const wallClock = () => Date.now();

export const useReadingTime = (
  wantAdId,
  {
    skip = false,
    send = recordWantAdReadingTime,
    now = wallClock,
    viewerKey = getReachViewerKey,
  } = {},
) => {
  useEffect(() => {
    const id = Number(wantAdId);
    if (!id || skip || typeof document === "undefined") return undefined;

    const key = viewerKey();
    let total = 0;
    let startedAt = null;
    let sentSeconds = 0;

    const isVisible = () => document.visibilityState !== "hidden";
    const pause = () => {
      if (startedAt === null) return;
      total = Math.min(READING_CAP_MS, total + Math.max(0, now() - startedAt));
      startedAt = null;
    };
    const resume = () => {
      if (startedAt === null && isVisible() && total < READING_CAP_MS) {
        startedAt = now();
      }
    };
    const flush = () => {
      const seconds = Math.floor(total / 1000);
      if (seconds < READING_MIN_SECONDS || seconds <= sentSeconds) return;
      sentSeconds = seconds;
      send(id, seconds, key);
    };
    const leave = () => {
      pause();
      flush();
    };
    const onVisibility = () => (isVisible() ? resume() : leave());

    resume();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", leave);
    window.addEventListener("pageshow", resume);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("pageshow", resume);
      leave();
    };
  }, [wantAdId, skip, send, now, viewerKey]);
};

export default useReadingTime;
